import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { v4 as uuid } from "uuid";

import { ALL_ITEMS_COLLECTION_ID, TranslationContext } from "@is/constants";
import { ViewStateContext } from "@is/contexts";
import { useCollection, useCollectionsList } from "@is/hooks";
import {
  Collection,
  CollectionStylesResponse,
  ContentItem,
  ContentItemType,
  ProductItem,
  WidgetType,
} from "@is/types";
import { createWidgetItem } from "@is/utils";

type Props = {
  onClose: () => void;
};

const useManageStoreFavoritesModal = ({ onClose }: Props) => {
  const { t } = useTranslation(TranslationContext.General);

  const { sections, updateViewState, intersectedItemsIds } =
    useContext(ViewStateContext).content;

  const [allItemsCollection, setAllItemsCollection] =
    useState<CollectionStylesResponse | null>(null);
  const [selectedCollectionId, setSelectedCollectionId] = useState<string>("");

  const { collection } = useCollection(selectedCollectionId);

  const { collections, getAllCollectionItems } = useCollectionsList({
    filterOutOfStockItems: true,
  });

  const collectionItems = useMemo(() => {
    const collectionsWithItems = collections?.items?.filter(
      (item) =>
        // The collection feeding the checkout "How about these?" section is reserved for that and is
        // kept out of the overview staff browse while selling. It stays listed in Store favorites
        // management and in the add-to-collection picker, which is how it is curated and released.
        !!item?.totalItems && !item?.isCheckoutSuggestionsCollection
    );

    return (
      collectionsWithItems?.map((item) => ({
        ...item,
        id: item.id ?? ALL_ITEMS_COLLECTION_ID,
      })) || []
    );
  }, [collections]);

  const selectedCollection = useMemo(
    () => collection || allItemsCollection,
    [allItemsCollection, collection]
  );

  const handleCollectionClick = useCallback(
    async (collectionId: string) => {
      if (collectionId === ALL_ITEMS_COLLECTION_ID) {
        const allItems = collectionItems.find(
          (item) => item.id === ALL_ITEMS_COLLECTION_ID
        );

        const newAllItemsCollection = await getAllCollectionItems(
          1,
          allItems.totalItems,
          true
        );

        setAllItemsCollection(newAllItemsCollection);
      } else {
        setSelectedCollectionId(collectionId);
      }
    },
    [collectionItems, getAllCollectionItems]
  );

  useEffect(() => {
    if (selectedCollection) {
      const itemsInStock = selectedCollection.items.filter(
        (item) => (item.hasInstoreStock || item.hasOnlineStock) && item.price
      );

      const items: ProductItem[] = itemsInStock?.map((item) => ({
        styleId: item.styleId,
        id: item.variantId,
        name: item.variantName,
        color: item.color.name,
        media: [{ url: item.mediaUrl }],
        price: item.price,
        isPDPOpen: true,
        ...(item.sizeInfos && { sizeInfos: item.sizeInfos }),
      }));

      const widgetItem = createWidgetItem({
        type: selectedCollectionId
          ? WidgetType.Collection
          : WidgetType.AllItems,
        items,
        ...(selectedCollectionId && {
          customTitle: (selectedCollection as Collection).name,
        }),
      });

      const contentItem: ContentItem = {
        type: ContentItemType.ContentWidgetItem,
        content: widgetItem,
        id: uuid(),
      };

      const lastIntersectedSection = sections[sections.length - 1];

      const lastIntersectedItem = lastIntersectedSection?.items
        .slice()
        .reverse()
        .find(
          (item: ContentItem) => intersectedItemsIds.indexOf(item.id) !== -1
        );

      const lastIntersectedItemIndex = lastIntersectedSection?.items?.findIndex(
        (item: ContentItem) => item.id === lastIntersectedItem?.id
      );

      lastIntersectedSection?.items?.splice(
        lastIntersectedItemIndex + 1,
        0,
        contentItem
      );

      const newSections = sections.map((section) =>
        lastIntersectedSection.id === section.id
          ? lastIntersectedSection
          : section
      );

      updateViewState({
        sections: newSections,
      });

      setSelectedCollectionId("");
      setAllItemsCollection(null);

      onClose();
    }
  }, [
    selectedCollection,
    onClose,
    sections,
    updateViewState,
    intersectedItemsIds,
    selectedCollectionId,
  ]);

  return { t, collectionItems, handleCollectionClick };
};

export default useManageStoreFavoritesModal;
